import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { Img } from '@euler/components/image/AdvancedImage';
import { config } from '@euler/config';
import { oemConfig } from '@euler/config/oem';
import {
  kPrivacyPolicyUrl,
  kTermsOfServiceUrl,
} from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { onErrorAlert, onErrorIgnore } from '@euler/utils';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import * as WebBrowser from 'expo-web-browser';
import { useCallback } from 'react';
import { Linking, ScrollView, View } from 'react-native';
import { Cell2 as Cell, Section } from '../components/SettingsTable';

const kIconColor = '#999';
const kIconSize = 24;

export const AboutScreen = wrapNavigatorScreen(
  () => {
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

    const { rpx } = useResponsiveContext();
    const iconSize = rpx(kIconSize);

    const { org, agent } = useCurrentUserDangerously();

    const phone =
      agent?.servicePhone ?? org.contactPhone ?? org.contactMobile ?? '';

    const onContactUsPress = useCallback(() => {
      if (!phone) return;
      Linking.openURL(`tel:${phone}`).catch(onErrorAlert);
    }, [phone]);

    const onPrivacyPolicyPress = useCallback(() => {
      WebBrowser.openBrowserAsync(
        oemConfig.privacyPolicyUrl ?? kPrivacyPolicyUrl,
      ).catch(onErrorIgnore);
    }, []);

    const onTermsOfServicePress = useCallback(() => {
      WebBrowser.openBrowserAsync(
        oemConfig.termsOfServiceUrl ?? kTermsOfServiceUrl,
      ).catch(onErrorIgnore);
    }, []);

    const onChangeLogPress = useCallback(() => {
      navigation.navigate('_releaseNotes', {});
    }, [navigation]);

    const onDebugPanelPress = useCallback(() => {
      navigation.navigate('_debugPanel', {});
    }, [navigation]);

    return (
      <ScrollView
        contentContainerStyle={{
          paddingTop: rpx(15),
          paddingBottom: rpx(15),
        }}
      >
        <View
          css={`
            margin-top: ${rpx(63)}px;
            margin-bottom: ${rpx(45)}px;
            align-items: center;
          `}
        >
          <Img
            uri={agent?.logoUrl ?? org.logoImgUrl}
            css={`
              width: ${rpx(75)}px;
              height: ${rpx(75)}px;
            `}
          />
          <Typography.Label
            light
            size={rpx(24)}
            color={'#555'}
            allowFontScaling
            css={`
              margin-top: ${rpx(21)}px;
              text-align: center;
            `}
          >
            {agent?.name ?? org.name}
          </Typography.Label>
        </View>

        <TableView>
          <Section>
            <Cell
              title="联系我们"
              icon={
                <Icon
                  name="phone"
                  size={iconSize}
                  color={kIconColor}
                  css={`
                    margin-left: ${rpx(5)}px;
                  `}
                />
              }
              detail={phone}
              detailIcon={'disclosure'}
              onPress={onContactUsPress}
            />
            <Cell
              title="隐私政策"
              icon={
                <Icon
                  name="privacy-policy"
                  size={iconSize}
                  color={kIconColor}
                  css={`
                    margin-left: ${rpx(5)}px;
                  `}
                />
              }
              detailIcon={'disclosure'}
              onPress={onPrivacyPolicyPress}
            />
            <Cell
              title="服务条款"
              icon={
                <Icon
                  name="terms-of-service"
                  size={iconSize}
                  color={kIconColor}
                  css={`
                    margin-left: ${rpx(5)}px;
                  `}
                />
              }
              detailIcon={'disclosure'}
              onPress={onTermsOfServicePress}
            />
            <Cell
              title="更新日志"
              icon={
                <Icon
                  name="changelog"
                  size={iconSize + rpx(4)}
                  color={kIconColor}
                  css={`
                    margin-left: ${rpx(2)}px;
                  `}
                />
              }
              detailIcon={'disclosure'}
              onPress={onChangeLogPress}
            />
            {config.environment !== 'production' && (
              <Cell
                title="调试信息"
                icon={
                  <Icon
                    name="file-tray-outline"
                    size={iconSize}
                    color={kIconColor}
                    css={`
                      width: ${iconSize + rpx(5)}px;
                    `}
                  />
                }
                detailIcon={'disclosure'}
                onPress={onDebugPanelPress}
              />
            )}
          </Section>
        </TableView>
      </ScrollView>
    );
  },
  {
    title: '关于我们',
  },
);
